<script setup lang="ts">
import type { Component } from "vue";
import type { IconName } from "../Icon/icons";
import { computed } from "vue";
import { useDialogLabel } from "../../composables/Dialog/useDialogLabel";
import VButton from "../Button/VButton.vue";
import VIcon from "../Icon/VIcon.vue";

const {
  title,
  message = "",
  confirmText = "Confirm",
  cancelText = "Cancel",
  icon = "",
  type = "primary",
  confirmLoading = false,
  confirmDisabled = false,
  hideCancel = false,
  hideConfirm = false,
  bodyProps = undefined,
  body = undefined,
} = defineProps<{
  title: string
  message?: string
  confirmText?: string
  cancelText?: string
  icon?: IconName
  type?: string
  confirmLoading?: boolean
  confirmDisabled?: boolean
  body?: Component
  bodyProps?: Record<string, unknown>
  hideCancel?: boolean
  hideConfirm?: boolean
}>();

const emit = defineEmits<{
  confirm: []
  cancel: []
}>();

const titleId = useDialogLabel("title");
const messageId = useDialogLabel("description", () => Boolean(message));

function typeIcon(): IconName {
  switch (type) {
    case "info": return "info";
    case "warning": return "triangle-alert";
    case "danger": return "circle-alert";
    case "success": return "circle-check";
    default: return "circle-question-mark";
  }
}

const buttonVariant = computed(() => {
  const optionVariants = {
    primary: "primary",
    secondary: "secondary",
    success: "success",
    info: "info",
    warning: "warning",
    danger: "danger",
  };
  if (optionVariants[type as keyof typeof optionVariants]) {
    return optionVariants[type as keyof typeof optionVariants] as "primary" | "secondary" | "success" | "info" | "warning" | "danger";
  }
  return "primary";
});

function handleConfirm() {
  emit("confirm");
}

function handleCancel() {
  emit("cancel");
}
</script>

<template>
  <div class="confirm-dialog-content">
    <div
      v-if="icon || type"
      class="confirm-icon"
    >
      <VIcon
        :name="icon || typeIcon()"
        :class="type"
        size="2em"
        aria-hidden="true"
      />
    </div>
    <strong :id="titleId" class="text-xl leading-6 text-balance">{{ title }}</strong>
    <div v-if="message" :id="messageId" class="description text-sm">
      {{ message }}
    </div>
    <component :is="body" v-if="body" v-bind="bodyProps" />
    <slot />
    <div
      class="confirm-actions"
      :class="hideCancel || hideConfirm ? 'justify-end' : 'justify-between'"
    >
      <VButton v-if="!hideCancel" variant="gray" rounded @click="handleCancel">
        {{ cancelText }}
      </VButton>
      <VButton
        v-if="!hideConfirm"
        :variant="buttonVariant"
        :loading="confirmLoading"
        :disabled="confirmDisabled"
        rounded
        @click="handleConfirm"
      >
        {{ confirmText }}
      </VButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
@mixin type-color($type) {
  .#{$type} {
    color: var(--#{$type});
  }
}

.confirm-dialog-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  width: 100%;
  min-height: 210px;
  gap: 1rem;
}

.confirm-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-actions {
  display: flex;
  // A label too long to sit next to the other button wraps the row, confirm on top, instead of scrolling the dialog.
  flex-wrap: wrap-reverse;
  width: 100%;
  gap: 0.5rem;
  margin-top: auto;
}

.confirm-actions > * {
  max-width: 100%;
}

.description {
  text-align: center;
  width: 100%;
  text-wrap: pretty;
  color: var(--text-2);
}

@include type-color("info");
@include type-color("warning");
@include type-color("danger");
@include type-color("primary");
@include type-color("secondary");
@include type-color("success");
</style>
